import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
  TextField,
  Alert,
  LinearProgress,
  Tooltip,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  CircularProgress,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SendIcon from '@mui/icons-material/Send';
import PsychologyIcon from '@mui/icons-material/Psychology';
import BuildIcon from '@mui/icons-material/Build';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckIcon from '@mui/icons-material/Check';
import { useMe } from '../api/hooks.js';
import { api } from '../api/client.js';
import { Markdown } from '../components/Markdown.js';
import { streamAsk, type TraceStep } from '../lib/askStream.js';
import { useT } from '../i18n/useT.js';

interface Message {
  role: 'user' | 'assistant';
  text: string;
  /** Which tools produced this answer, for the "worked from" line. */
  trace?: TraceStep[];
  model?: string | null;
  exhausted?: boolean;
}

interface ConversationSummary {
  id: string;
  title: string;
  updatedAt: string;
}

/** The answer while it is being produced. */
interface Live {
  text: string;
  steps: { id: string; name: string; done: boolean; failed: boolean }[];
}

/**
 * Ask the recordings a question.
 *
 * The answer is worth no more than the data it came from, so every reply
 * carries the tools it ran. Without that line an invented number and a
 * measured one look identical, and this page reports numbers people act on.
 *
 * Conversations are kept on the server, one list per person, and the open one
 * is in the URL (`?c=`) so a reload or a shared tab lands on the same place.
 */
export function AskPage() {
  const t = useT().ask;
  const qc = useQueryClient();
  const { data: me } = useMe();
  const orgId = me?.organizations[0]?.id;
  const base = `/v1/orgs/${orgId}/assistant`;

  const [params, setParams] = useSearchParams();
  const conversationId = params.get('c') ?? undefined;

  const [messages, setMessages] = useState<Message[]>([]);
  const [draft, setDraft] = useState('');
  const [live, setLive] = useState<Live | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const pending = live !== null;

  const status = useQuery({
    enabled: !!orgId,
    queryKey: ['assistant-status', orgId],
    queryFn: async () => (await api.get(`${base}/status`)).data as { enabled: boolean; reason?: string },
  });

  const list = useQuery({
    enabled: !!orgId,
    queryKey: ['assistant-conversations', orgId],
    queryFn: async () => (await api.get(`${base}/conversations`)).data.data as ConversationSummary[],
  });

  const open = useQuery({
    enabled: !!orgId && !!conversationId,
    queryKey: ['assistant-conversation', orgId, conversationId],
    queryFn: async () =>
      (await api.get(`${base}/conversations/${conversationId}`)).data as { messages: Message[] },
    // Refetching while an answer streams in would replace it with the stored
    // copy, which does not have it yet. Saved answers do not change on their own.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  useEffect(() => {
    if (!conversationId) setMessages([]);
    else if (open.data) setMessages(open.data.messages);
  }, [conversationId, open.data]);

  // Follow the conversation down as it grows, including while one is running.
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length, live?.steps.length, live?.text.length]);

  const select = (id?: string) => {
    if (pending) return;
    setFailure(null);
    setParams(id ? { c: id } : {});
  };

  const remove = async (id: string) => {
    if (!window.confirm(t.deleteConfirm)) return;
    await api.delete(`${base}/conversations/${id}`);
    qc.removeQueries({ queryKey: ['assistant-conversation', orgId, id] });
    await qc.invalidateQueries({ queryKey: ['assistant-conversations', orgId] });
    if (id === conversationId) select(undefined);
  };

  const send = async (text: string) => {
    const question = text.trim();
    if (!question || pending) return;
    setMessages((prev) => [...prev, { role: 'user', text: question }]);
    setDraft('');
    setFailure(null);
    setLive({ text: '', steps: [] });
    try {
      await streamAsk(`${base}/ask`, { conversationId, question }, (e) => {
        if (e.type === 'text') {
          setLive((l) => l && { ...l, text: l.text + e.delta });
        } else if (e.type === 'tool' && e.phase === 'start') {
          // Prose written before a tool call is the model thinking aloud
          // ("let me check…"); the answer is what comes after the tools.
          setLive((l) => l && { text: '', steps: [...l.steps, { id: e.id, name: e.name, done: false, failed: false }] });
        } else if (e.type === 'tool' && e.phase === 'end') {
          setLive(
            (l) =>
              l && { ...l, steps: l.steps.map((s) => (s.id === e.id ? { ...s, done: true, failed: e.failed } : s)) },
          );
        } else if (e.type === 'done') {
          const answer: Message = {
            role: 'assistant',
            text: e.text,
            trace: e.trace,
            model: e.model,
            exhausted: e.exhausted,
          };
          const saved = e.conversation;
          setMessages((prev) => {
            const next = [...prev, answer];
            // Seeding the cache with what is on screen means opening the new
            // conversation's URL shows it at once instead of refetching it.
            if (saved) qc.setQueryData(['assistant-conversation', orgId, saved.id], { messages: next });
            return next;
          });
          if (saved) {
            void qc.invalidateQueries({ queryKey: ['assistant-conversations', orgId] });
            if (saved.id !== conversationId) setParams({ c: saved.id }, { replace: true });
          }
        } else if (e.type === 'error') {
          setFailure(e.message);
        }
      });
    } catch (err) {
      setFailure((err as Error).message || t.failed);
    } finally {
      setLive(null);
    }
  };

  const blocked = status.data && !status.data.enabled;
  const conversations = list.data ?? [];

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
        <ChatBubbleOutlineIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>
          {t.title}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        {t.subtitle}
      </Typography>

      {blocked && (
        <Alert severity="info" icon={<PsychologyIcon />} sx={{ mb: 2 }}>
          {status.data?.reason}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems="flex-start">
        <Paper variant="outlined" sx={{ width: { xs: '100%', md: 260 }, flexShrink: 0, overflow: 'hidden' }}>
          <Box sx={{ p: 1.25 }}>
            <Button
              fullWidth
              size="small"
              variant={conversationId ? 'outlined' : 'contained'}
              startIcon={<AddIcon />}
              disabled={pending}
              onClick={() => select(undefined)}
            >
              {t.newChat}
            </Button>
          </Box>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ px: 1.75, display: 'block', letterSpacing: '0.08em' }}
          >
            {t.history}
          </Typography>
          {conversations.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.75, pb: 1.5 }}>
              {t.noHistory}
            </Typography>
          ) : (
            <List dense disablePadding sx={{ maxHeight: { md: '60vh' }, overflowY: 'auto', pb: 0.5 }}>
              {conversations.map((c) => (
                <ListItemButton
                  key={c.id}
                  selected={c.id === conversationId}
                  disabled={pending && c.id !== conversationId}
                  onClick={() => select(c.id)}
                  sx={{ pr: 0.5, '& .ask-del': { opacity: 0 }, '&:hover .ask-del, &.Mui-selected .ask-del': { opacity: 1 } }}
                >
                  <ListItemText
                    primary={c.title}
                    secondary={new Date(c.updatedAt).toLocaleDateString()}
                    primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <Tooltip title={t.delete}>
                    <IconButton
                      className="ask-del"
                      size="small"
                      aria-label={t.delete}
                      disabled={pending}
                      onClick={(e) => {
                        e.stopPropagation();
                        void remove(c.id);
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemButton>
              ))}
            </List>
          )}
        </Paper>

        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          {open.isError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t.notFound}
            </Alert>
          )}
          {conversationId && open.isLoading && <LinearProgress sx={{ mb: 2 }} />}

          {messages.length === 0 && !conversationId && !blocked && (
            <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                {t.examplesTitle}
              </Typography>
              <Stack spacing={1} alignItems="flex-start">
                {t.examples.map((q) => (
                  <Button
                    key={q}
                    size="small"
                    variant="outlined"
                    onClick={() => void send(q)}
                    sx={{ textTransform: 'none', textAlign: 'left', justifyContent: 'flex-start' }}
                  >
                    {q}
                  </Button>
                ))}
              </Stack>
            </Paper>
          )}

          <Stack spacing={2} sx={{ mb: 2 }}>
            {messages.map((m, i) => (
              <Bubble key={i} m={m} workedFrom={t.workedFrom} exhausted={t.exhausted} />
            ))}

            {live && (
              <Paper variant="outlined" sx={{ p: 2, maxWidth: '46rem', alignSelf: 'flex-start', width: '100%' }}>
                {live.steps.length > 0 && (
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: live.text ? 1.5 : 0 }}>
                    {live.steps.map((s) => (
                      <Chip
                        key={s.id}
                        size="small"
                        variant="outlined"
                        color={s.failed ? 'warning' : 'default'}
                        icon={s.done ? <CheckIcon /> : <CircularProgress size={12} sx={{ ml: 0.75 }} />}
                        label={t.tools[s.name] ?? s.name}
                      />
                    ))}
                  </Stack>
                )}
                {live.text ? (
                  <Markdown text={live.text} />
                ) : (
                  <Box sx={{ mt: live.steps.length ? 1.5 : 0 }}>
                    <LinearProgress />
                    <Typography variant="caption" color="text.secondary">
                      {live.steps.length && live.steps.every((s) => s.done) ? t.writing : t.thinking}
                    </Typography>
                  </Box>
                )}
              </Paper>
            )}
            <div ref={endRef} />
          </Stack>

          {failure && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {failure}
            </Alert>
          )}

          <Stack direction="row" spacing={1} alignItems="flex-end">
            <TextField
              fullWidth
              multiline
              maxRows={6}
              size="small"
              placeholder={t.placeholder}
              value={draft}
              disabled={blocked || pending}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                // Enter sends, Shift+Enter breaks the line — what a chat box does.
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send(draft);
                }
              }}
            />
            <Button
              variant="contained"
              endIcon={<SendIcon />}
              disabled={blocked || pending || !draft.trim()}
              onClick={() => void send(draft)}
            >
              {t.send}
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {t.scope}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

function Bubble({ m, workedFrom, exhausted }: { m: Message; workedFrom: string; exhausted: string }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        maxWidth: '46rem',
        alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
        bgcolor: m.role === 'user' ? 'action.hover' : 'background.paper',
      }}
    >
      {m.role === 'user' ? (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {m.text}
        </Typography>
      ) : (
        <>
          <Markdown text={m.text} />
          {m.exhausted && (
            <Alert severity="warning" icon={<ErrorOutlineIcon />} sx={{ mt: 1.5 }}>
              {exhausted}
            </Alert>
          )}
          {!!m.trace?.length && (
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 1.5 }}>
              <BuildIcon fontSize="inherit" color="disabled" />
              <Typography variant="caption" color="text.secondary">
                {workedFrom}
              </Typography>
              {m.trace.map((step, j) => (
                <Tooltip key={j} title={JSON.stringify(step.args)}>
                  <Chip size="small" variant="outlined" label={step.name} color={step.failed ? 'warning' : 'default'} />
                </Tooltip>
              ))}
              {m.model && (
                <Typography variant="caption" color="text.disabled">
                  · {m.model}
                </Typography>
              )}
            </Stack>
          )}
        </>
      )}
    </Paper>
  );
}
